import { motion } from "framer-motion";
import {
  CheckSquare, TrendingUp, TrendingDown,
  Clock, Flame, Wallet
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import KpiCard from "./KpiCard";

// Sessão UI-1 — DashboardSummary refatorado pra 3 KpiCards heroicos.
//
// IMPORTANTE — gaps de schema do /api/dashboard atual:
//   - tasks_summary expõe pending + overdue, NÃO done/total. KPI Tarefas
//     mostra `pending` como valor heroic + badge "Atrasadas: N" quando
//     overdue > 0 (mais honesto que inventar done/total).
//   - habits_today expõe checked + total — usados como "checked/total".
//     Streak em dias (streak_current/streak_best do brief) NÃO está no
//     payload — microcopy e badge derivam do progresso do DIA, não streak.
//   - financial_summary expõe income/expenses/balance — sem avg_balance
//     ou prev_month_diff. Microcopy compara expenses vs income; badge
//     mostra delta receita-despesa quando positivo/negativo.
//
// Os exports legados (SummaryCard, RecentActivity) ficam pra back-compat
// caso alguém os tenha importado fora — ninguém usa hoje, mas removo só
// na próxima sessão (segurança).

// Summary Card Component (LEGACY — mantido pra back-compat).
export function SummaryCard({
  icon: Icon,
  title,
  value,
  subtitle,
  trend,
  color = "cyan",
  onClick,
  delay = 0,
}) {
  const colorClasses = {
    cyan:    { iconBg: "bg-brand-cyan/10",    iconColor: "text-brand-cyan",    border: "hover:border-brand-cyan/30",    glow: "hover:shadow-glow-cyan" },
    purple:  { iconBg: "bg-brand-purple/10",  iconColor: "text-brand-purple",  border: "hover:border-brand-purple/30",  glow: "hover:shadow-glow-purple" },
    green:   { iconBg: "bg-brand-green/10",   iconColor: "text-brand-green",   border: "hover:border-brand-green/30",   glow: "hover:shadow-glow-green" },
    warning: { iconBg: "bg-status-warning/10",iconColor: "text-status-warning",border: "hover:border-status-warning/30",glow: "hover:shadow-lg hover:shadow-status-warning/10" },
    error:   { iconBg: "bg-status-error/10",  iconColor: "text-status-error",  border: "hover:border-status-error/30",  glow: "hover:shadow-lg hover:shadow-status-error/10" },
  };
  const styles = colorClasses[color] || colorClasses.cyan;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ y: -4, scale: 1.02 }} whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn("bg-nectar-card border border-nectar-border rounded-xl p-5 transition-all duration-300 cursor-pointer group", styles.border, styles.glow)}
      data-testid={`summary-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <motion.div className={cn("w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110", styles.iconBg)} whileHover={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 0.4 }}>
          <Icon className={cn("w-5 h-5", styles.iconColor)} />
        </motion.div>
        {trend != null && (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: delay + 0.2 }} className={cn("flex items-center gap-1 text-xs", trend > 0 ? "text-brand-green" : "text-status-error")}>
            {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{Math.abs(trend)}%</span>
          </motion.div>
        )}
      </div>
      <motion.h3 className="text-2xl font-bold text-white mb-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.1 }}>{value}</motion.h3>
      <p className="text-sm text-slate-400 mb-1">{title}</p>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </motion.div>
  );
}

// Microcopy helpers — pt-BR profissional, sem gírias.
function tasksMicrocopy(pending, overdue) {
  if (pending === 0 && overdue === 0) return "Nenhuma tarefa pendente. Bom trabalho.";
  if (overdue > 0 && overdue === pending) return `${overdue} tarefa${overdue === 1 ? "" : "s"} em atraso.`;
  if (overdue > 0) return `${pending} pendente${pending === 1 ? "" : "s"} — ${overdue} em atraso.`;
  return `${pending} pendente${pending === 1 ? "" : "s"} hoje.`;
}

function habitsMicrocopy(checked, total) {
  if (total === 0) return "Configure seus hábitos pra começar.";
  if (checked === 0) return "Comece hoje. Pequenos passos.";
  if (checked === total) return "Hábitos do dia concluídos. Excelente.";
  const remaining = total - checked;
  if (checked / total >= 0.5) return `Falta pouco — ${remaining} hábito${remaining === 1 ? "" : "s"} restante${remaining === 1 ? "" : "s"}.`;
  return `${remaining} hábito${remaining === 1 ? "" : "s"} pra concluir hoje.`;
}

function balanceMicrocopy(balance, income, expenses) {
  if (income === 0 && expenses === 0) return "Sem movimentação registrada este mês.";
  if (balance < 0) return "Despesas acima das receitas este mês.";
  if (income > 0 && expenses / income > 0.8) return "Margem apertada — despesas próximas das receitas.";
  return "Receitas superando despesas. Ritmo saudável.";
}

function fmtBRL(v) {
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

// Dashboard Summary Grid — 3 KpiCards heroicos
export function DashboardSummary({ data, onCardClick }) {
  const pending = data?.pendingTasks ?? 0;
  const overdue = data?.tasksOverdue ?? 0;
  const habitsChecked = data?.habitsCheckedToday ?? 0;
  const habitsTotal = data?.habitsTotalToday ?? 0;
  const balance = data?.monthBalance ?? 0;
  const income = data?.monthIncome ?? 0;
  const expenses = data?.monthExpenses ?? 0;

  // Tarefas: badge condicional
  const tasksBadge = overdue > 0
    ? <Badge variant="danger">{overdue} atrasada{overdue === 1 ? "" : "s"}</Badge>
    : pending === 0
      ? <Badge variant="success">Em dia</Badge>
      : null;

  // Hábitos: badge mostra %% concluído (sem streak no payload atual)
  const habitsPct = habitsTotal > 0 ? Math.round((habitsChecked / habitsTotal) * 100) : 0;
  const habitsBadge = habitsTotal > 0
    ? habitsChecked === habitsTotal
      ? <Badge variant="success">Completo</Badge>
      : <Badge variant="warning">{habitsPct}%</Badge>
    : null;

  // Saldo: badge variação receita - despesa (delta absoluto)
  const balanceBadge = (income > 0 || expenses > 0)
    ? balance >= 0
      ? <Badge variant="success">+{fmtBRL(Math.abs(balance))}</Badge>
      : <Badge variant="danger">−{fmtBRL(Math.abs(balance))}</Badge>
    : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <KpiCard
        icon={CheckSquare}
        label="TAREFAS DE HOJE"
        value={pending}
        microcopy={tasksMicrocopy(pending, overdue)}
        badge={tasksBadge}
        tone="cyan"
        onClick={() => onCardClick?.("tasks")}
        delay={0}
        testId="kpi-tasks"
      />
      <KpiCard
        icon={Flame}
        label="HÁBITOS"
        value={habitsTotal > 0 ? `${habitsChecked}/${habitsTotal}` : "—"}
        microcopy={habitsMicrocopy(habitsChecked, habitsTotal)}
        badge={habitsBadge}
        tone="amber"
        onClick={() => onCardClick?.("habits")}
        delay={0.08}
        testId="kpi-habits"
      />
      <KpiCard
        icon={Wallet}
        label="SALDO DO MÊS"
        value={fmtBRL(balance)}
        valueClassName={cn(
          "text-[56px] leading-none tracking-[-0.02em]",
          balance >= 0 ? "text-brand-green" : "text-status-error",
        )}
        microcopy={balanceMicrocopy(balance, income, expenses)}
        badge={balanceBadge}
        tone={balance >= 0 ? "green" : "error"}
        onClick={() => onCardClick?.("finance")}
        delay={0.16}
        testId="kpi-finance"
      />
    </div>
  );
}

// Recent Activity (LEGACY — mantido pra back-compat, não usado no Dashboard atual).
export function RecentActivity({ activities = [], onActivityClick }) {
  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Nenhuma atividade recente</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {activities.map((activity, index) => (
        <motion.div
          key={activity.id || index}
          initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={() => onActivityClick?.(activity)}
          className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] cursor-pointer transition-colors"
        >
          <div className={cn("w-2 h-2 rounded-full", activity.type === 'task' ? 'bg-brand-cyan' : activity.type === 'reminder' ? 'bg-brand-purple' : activity.type === 'finance' ? 'bg-brand-green' : 'bg-slate-500')} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-300 truncate">{activity.title}</p>
            <p className="text-xs text-slate-500">{activity.time}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export default { SummaryCard, DashboardSummary, RecentActivity };
